import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { CheckCircle2, Loader2, Mail, Phone, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { otpSendFailureToast, otpSendSuccessToast } from '../lib/otpSendMessages';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from './ui/input-otp';

const OTP_LENGTH = 6;

function extractError(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg || d).join(', ');
  return fallback;
}

function sessionKey(channel) {
  return `dhansthal_otp_session_${channel}`;
}

function readOtpSession(channel) {
  try {
    const raw = sessionStorage.getItem(sessionKey(channel));
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || typeof data !== 'object') return null;
    return data;
  } catch {
    return null;
  }
}

function writeOtpSession(channel, data) {
  try {
    sessionStorage.setItem(sessionKey(channel), JSON.stringify(data));
  } catch {
    /* ignore quota */
  }
}

function clearOtpSession(channel) {
  try {
    sessionStorage.removeItem(sessionKey(channel));
  } catch {
    /* ignore */
  }
}

function VerifiedChip() {
  return (
    <Badge
      className="border-emerald-200 bg-emerald-50 text-emerald-700 gap-1 px-2 py-0.5 text-[10px] font-semibold tracking-wide uppercase shadow-none"
      data-testid="verified-chip"
    >
      <CheckCircle2 className="w-3 h-3" />
      Verified
    </Badge>
  );
}

function UnverifiedChip() {
  return (
    <Badge
      className="border-amber-200 bg-amber-50 text-amber-700 gap-1 px-2 py-0.5 text-[10px] font-semibold tracking-wide uppercase shadow-none"
      data-testid="unverified-chip"
    >
      Pending
    </Badge>
  );
}

const verifyBtnClass =
  'rounded-lg h-8 px-3 border border-[#1B6EF3]/30 bg-white text-[#1B6EF3] hover:bg-[#1B6EF3] hover:text-white hover:border-[#1B6EF3] shadow-none transition-colors';

/**
 * OTP verification dialog for email or mobile.
 *
 * Close/reopen rules:
 * - Server /api/otp/status is the source of truth for timers + delivery_skipped
 * - Autofill ONLY when delivery_skipped=true (OTP_SKIP_DELIVERY=true)
 * - If an OTP is still valid (or cooldown active), do NOT send again on reopen
 * - Real delivery mode never stores/restores OTP digits
 */
export function OtpVerifyDialog({
  open,
  onOpenChange,
  channel,
  destinationHint,
  mobileValue,
  onVerified,
}) {
  const [otp, setOtp] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [expiresIn, setExpiresIn] = useState(0);
  const [masked, setMasked] = useState(destinationHint || '');
  const [deliverySkipped, setDeliverySkipped] = useState(false);
  const [phase, setPhase] = useState('idle'); // idle | preparing | ready | waiting
  const bootstrappedRef = useRef(false);
  const inputWrapRef = useRef(null);
  const sendingRef = useRef(false);

  const isEmail = channel === 'email';

  const focusFirstSlot = useCallback(() => {
    requestAnimationFrame(() => {
      const el = inputWrapRef.current?.querySelector('input');
      if (el) el.focus();
    });
  }, []);

  const applyTimers = useCallback((expiresSec, cooldownSec) => {
    const exp = Math.max(0, Number(expiresSec) || 0);
    const cd = Math.max(0, Number(cooldownSec) || 0);
    setExpiresIn(exp);
    setCooldown(cd);
    if (exp > 0) setPhase('ready');
    else if (cd > 0) setPhase('waiting');
    else setPhase('idle');
  }, []);

  const readChannelTimers = useCallback((status) => {
    if (!status) return { expiresLeft: 0, cooldownLeft: 0, skipped: false };
    return {
      expiresLeft: Number(
        isEmail ? status.email_otp_expires_in_seconds : status.mobile_otp_expires_in_seconds
      ) || 0,
      cooldownLeft: Number(
        isEmail ? status.email_resend_after_seconds : status.mobile_resend_after_seconds
      ) || 0,
      skipped: !!status.delivery_skipped,
    };
  }, [isEmail]);

  const persistSession = useCallback((payload) => {
    const now = Date.now();
    const expiresSec = Number(payload.expires_in_seconds) || 600;
    const cooldownSec = Number(payload.resend_after_seconds) || 60;
    const skipped = !!payload.delivery_skipped;
    // Never keep OTP digits unless server explicitly skipped delivery
    const autofill = skipped ? (payload.autofill_otp || payload.otp || null) : null;
    writeOtpSession(channel, {
      expiresAt: now + expiresSec * 1000,
      cooldownUntil: now + cooldownSec * 1000,
      masked: payload.destination_masked || destinationHint || '',
      deliverySkipped: skipped,
      autofillOtp: autofill,
    });
    setDeliverySkipped(skipped);
    setMasked(payload.destination_masked || destinationHint || '');
    applyTimers(expiresSec, cooldownSec);
    setOtp(autofill ? String(autofill) : '');
  }, [applyTimers, channel, destinationHint]);

  const syncSessionFromStatus = useCallback((status, { softToast = false } = {}) => {
    const { expiresLeft, cooldownLeft, skipped } = readChannelTimers(status);
    const saved = readOtpSession(channel) || {};
    setDeliverySkipped(skipped);
    setMasked(saved.masked || destinationHint || '');
    applyTimers(expiresLeft, cooldownLeft);

    // Strip any leftover autofill if we are in real-delivery mode
    const autofill = skipped && expiresLeft > 0 ? (saved.autofillOtp || null) : null;
    writeOtpSession(channel, {
      expiresAt: Date.now() + expiresLeft * 1000,
      cooldownUntil: Date.now() + cooldownLeft * 1000,
      masked: saved.masked || destinationHint || '',
      deliverySkipped: skipped,
      autofillOtp: autofill,
    });
    setOtp(autofill ? String(autofill) : '');

    if (softToast && (expiresLeft > 0 || cooldownLeft > 0)) {
      if (expiresLeft > 0) {
        toast.message(
          skipped
            ? 'Previous OTP is still valid'
            : 'OTP already sent — enter the code from your email/SMS'
        );
      } else if (cooldownLeft > 0) {
        toast.message(`Please wait ${cooldownLeft}s before requesting another OTP`);
      }
    }

    return { expiresLeft, cooldownLeft, skipped };
  }, [applyTimers, channel, destinationHint, readChannelTimers]);

  const sendOtp = useCallback(async ({ force = false, silentCooldownToast = false } = {}) => {
    if (sendingRef.current) return;
    if (!force) {
      try {
        const st = (await api.get('/api/otp/status')).data;
        const { expiresLeft, cooldownLeft } = readChannelTimers(st);
        if (cooldownLeft > 0) {
          syncSessionFromStatus(st, { softToast: !silentCooldownToast });
          focusFirstSlot();
          return;
        }
        // Still have a valid OTP and user clicked resend with force=false after cooldown ended
        // (cooldown 0) — allow resend. If somehow cooldown 0 but they shouldn't resend mid-entry,
        // that's fine.
        void expiresLeft;
      } catch {
        /* proceed to send */
      }
    }

    sendingRef.current = true;
    setSending(true);
    setPhase('preparing');
    try {
      const payload = { channel };
      if (!isEmail && mobileValue) payload.mobile = mobileValue;
      const res = await api.post('/api/otp/send', payload);
      persistSession(res.data);
      toast.success(otpSendSuccessToast({ isEmail, data: res.data }));
      focusFirstSlot();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        try {
          const st = (await api.get('/api/otp/status')).data;
          syncSessionFromStatus(st, { softToast: false });
        } catch {
          /* ignore */
        }
      } else {
        setPhase('idle');
      }
      toast.error(
        otpSendFailureToast({
          isEmail,
          fallbackDetail: extractError(err, null),
        })
      );
      focusFirstSlot();
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  }, [
    channel,
    focusFirstSlot,
    isEmail,
    mobileValue,
    persistSession,
    readChannelTimers,
    syncSessionFromStatus,
  ]);

  // Bootstrap on open: status first, then restore or send
  useEffect(() => {
    if (!open) {
      bootstrappedRef.current = false;
      sendingRef.current = false;
      setOtp('');
      setSending(false);
      setVerifying(false);
      setCooldown(0);
      setExpiresIn(0);
      setDeliverySkipped(false);
      setPhase('idle');
      setMasked(destinationHint || '');
      return undefined;
    }

    if (bootstrappedRef.current) return undefined;
    bootstrappedRef.current = true;
    setPhase('preparing');
    setMasked(destinationHint || '');

    let cancelled = false;

    (async () => {
      try {
        const st = (await api.get('/api/otp/status')).data;
        if (cancelled) return;
        const { expiresLeft, cooldownLeft } = syncSessionFromStatus(st, { softToast: false });

        // Active OTP or cooldown → do not send again (real or skip mode)
        if (expiresLeft > 0 || cooldownLeft > 0) {
          if (expiresLeft > 0) {
            toast.message(
              st.delivery_skipped
                ? 'Previous OTP is still valid'
                : 'OTP already sent — enter the code from your email/SMS'
            );
          } else {
            toast.message(`Please wait ${cooldownLeft}s before requesting another OTP`);
          }
          focusFirstSlot();
          return;
        }

        // Fresh send
        await sendOtp({ force: true });
      } catch {
        if (cancelled) return;
        // Status failed — fall back to send
        await sendOtp({ force: true });
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || cooldown <= 0) return undefined;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [open, cooldown > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || expiresIn <= 0) return undefined;
    const t = setInterval(() => {
      setExpiresIn((c) => {
        const next = Math.max(0, c - 1);
        if (next <= 0) {
          setPhase((p) => (p === 'ready' ? 'waiting' : p));
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [open, expiresIn > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleVerify = async () => {
    if (verifying || sending) return;
    if (otp.length !== OTP_LENGTH) {
      toast.error(`Enter the ${OTP_LENGTH}-digit OTP`);
      return;
    }
    setVerifying(true);
    try {
      const res = await api.post('/api/otp/verify', { channel, otp });
      clearOtpSession(channel);
      toast.success(res.data.message || 'Verified successfully');
      onVerified?.(res.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(extractError(err, 'Verification failed'));
      setOtp('');
      focusFirstSlot();
    } finally {
      setVerifying(false);
    }
  };

  const handleVerifySubmit = (e) => {
    e.preventDefault();
    handleVerify();
  };

  const statusLabel = (() => {
    if (sending || phase === 'preparing') return 'Sending code…';
    if (expiresIn > 0) {
      return `Code expires in ${Math.floor(expiresIn / 60)}:${String(expiresIn % 60).padStart(2, '0')}`;
    }
    if (cooldown > 0) return `Resend available in ${cooldown}s`;
    if (phase === 'waiting' || phase === 'idle') return 'Request a new code';
    return 'Preparing code…';
  })();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md rounded-2xl border-slate-200 p-0 overflow-hidden"
        data-testid="otp-verify-dialog"
        closeButtonClassName="text-white/90 hover:text-white hover:bg-white/15 opacity-100 data-[state=open]:bg-transparent data-[state=open]:text-white right-3 top-3 rounded-lg p-1.5"
      >
        <div className="bg-gradient-to-br from-[#0B1A33] via-[#132848] to-[#1B3A5F] px-6 pt-6 pb-5 text-white pr-12">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center ring-1 ring-white/20">
              <ShieldCheck className="w-5 h-5 text-sky-200" />
            </div>
            <DialogHeader className="space-y-1 text-left">
              <DialogTitle className="text-white text-lg font-semibold tracking-tight">
                Verify {isEmail ? 'email' : 'mobile'}
              </DialogTitle>
              <DialogDescription className="text-slate-300 text-sm">
                Enter the {OTP_LENGTH}-digit code for{' '}
                <span className="text-white font-medium">{masked || 'your contact'}</span>
                {deliverySkipped ? (
                  <span className="block mt-1 text-sky-200/90 text-xs">Delivery skipped (test mode) — code autofilled</span>
                ) : null}
              </DialogDescription>
            </DialogHeader>
          </div>
        </div>

        <form className="px-6 py-5 space-y-5 bg-white" onSubmit={handleVerifySubmit}>
          <div className="flex justify-center" ref={inputWrapRef}>
            <InputOTP
              maxLength={OTP_LENGTH}
              value={otp}
              onChange={setOtp}
              disabled={verifying || sending}
              autoFocus
              data-testid="otp-input"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleVerify();
                }
              }}
            >
              <InputOTPGroup className="gap-2">
                {Array.from({ length: OTP_LENGTH }).map((_, i) => (
                  <InputOTPSlot
                    key={i}
                    index={i}
                    className="h-11 w-10 sm:w-11 rounded-xl border border-slate-200 text-base font-semibold first:rounded-xl last:rounded-xl shadow-sm"
                  />
                ))}
              </InputOTPGroup>
            </InputOTP>
          </div>

          <div className="flex items-center justify-between text-xs text-slate-500 gap-2">
            <span>{statusLabel}</span>
            <button
              type="button"
              disabled={sending || cooldown > 0}
              onClick={() => sendOtp({ force: false })}
              className="font-medium text-[#1B6EF3] hover:text-[#1557c0] disabled:text-slate-400 disabled:cursor-not-allowed transition-colors shrink-0"
              data-testid="otp-resend"
            >
              {sending ? 'Sending…' : cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend OTP'}
            </button>
          </div>

          <Button
            type="submit"
            className="w-full h-11 rounded-xl bg-[#0B1A33] hover:bg-[#152a4a] text-white font-medium"
            disabled={verifying || otp.length !== OTP_LENGTH}
            data-testid="otp-verify-btn"
          >
            {verifying ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Verifying…
              </>
            ) : (
              'Confirm & verify'
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

OtpVerifyDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  channel: PropTypes.oneOf(['email', 'mobile']).isRequired,
  destinationHint: PropTypes.string,
  mobileValue: PropTypes.string,
  onVerified: PropTypes.func,
};

/**
 * Registration / profile contact verification controls.
 */
export function ContactVerificationControls({
  email,
  mobile,
  emailVerified,
  mobileVerified,
  mobileEditable = true,
  onMobileChange,
  onVerificationChange,
  showGateMessage = false,
  gateMessage = '',
  mode = 'onboarding',
}) {
  const [emailDialog, setEmailDialog] = useState(false);
  const [mobileDialog, setMobileDialog] = useState(false);
  const [localMobile, setLocalMobile] = useState(mobile || '');
  const [savingMobile, setSavingMobile] = useState(false);

  useEffect(() => {
    setLocalMobile(mobile || '');
  }, [mobile]);

  const handleVerified = (data) => {
    if (data?.channel === 'email' || data?.is_email_verified) clearOtpSession('email');
    if (data?.channel === 'mobile' || data?.is_mobile_verified) clearOtpSession('mobile');
    onVerificationChange?.(data);
  };

  const canVerifyMobile = Boolean((localMobile || '').trim()) && (localMobile || '').replace(/\D/g, '').length === 10;

  const saveMobileIfNeeded = async () => {
    if (mode !== 'profile') return true;
    if ((localMobile || '') === (mobile || '')) return true;
    setSavingMobile(true);
    try {
      const res = await api.post('/api/otp/mobile', { mobile: localMobile || null });
      clearOtpSession('mobile');
      onMobileChange?.(res.data.mobile || '');
      onVerificationChange?.({
        is_email_verified: emailVerified,
        is_mobile_verified: res.data.is_mobile_verified,
        mobile: res.data.mobile,
      });
      toast.success(res.data.message || 'Mobile updated');
      return true;
    } catch (err) {
      toast.error(extractError(err, 'Failed to update mobile'));
      return false;
    } finally {
      setSavingMobile(false);
    }
  };

  const openMobileVerify = async () => {
    if (!canVerifyMobile) {
      toast.error('Enter a valid 10-digit mobile number first');
      return;
    }
    if (mode === 'profile') {
      const ok = await saveMobileIfNeeded();
      if (!ok) return;
    }
    setMobileDialog(true);
  };

  return (
    <div className="space-y-4" data-testid="contact-verification-controls">
      {/* Email */}
      <div className="rounded-2xl border border-slate-200/80 bg-white p-3.5 sm:p-4">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 w-9 h-9 rounded-xl bg-[#e8eff8] text-[#3a6bb5] flex items-center justify-center shrink-0">
            <Mail className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0 space-y-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <label htmlFor="verify-email" className="text-sm font-medium text-slate-700">
                Email {mode === 'onboarding' ? '*' : ''}
              </label>
              {emailVerified ? <VerifiedChip /> : <UnverifiedChip />}
            </div>
            <Input
              id="verify-email"
              value={email || ''}
              disabled
              className="rounded-xl bg-slate-50/80 border-slate-200"
              data-testid="verify-email-input"
            />
            {!emailVerified && (
              <div className="flex justify-end">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className={verifyBtnClass}
                  onClick={() => setEmailDialog(true)}
                  data-testid="verify-email-btn"
                >
                  Verify email
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile */}
      <div className="rounded-2xl border border-slate-200/80 bg-white p-3.5 sm:p-4">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 w-9 h-9 rounded-xl bg-[#e8eff8] text-[#3a6bb5] flex items-center justify-center shrink-0">
            <Phone className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0 space-y-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <label htmlFor="verify-mobile" className="text-sm font-medium text-slate-700">
                Mobile {mode === 'profile' ? '' : '(optional)'}
              </label>
              {(localMobile || mobile) ? (
                mobileVerified ? <VerifiedChip /> : <UnverifiedChip />
              ) : null}
            </div>
            <div className="flex gap-2">
              <Input
                id="verify-mobile"
                value={localMobile}
                disabled={!mobileEditable}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, '').slice(0, 10);
                  setLocalMobile(v);
                  onMobileChange?.(v);
                }}
                placeholder="10-digit mobile"
                className="rounded-xl border-slate-200"
                maxLength={10}
                inputMode="numeric"
                data-testid="verify-mobile-input"
              />
              {mode === 'profile' && mobileEditable && (localMobile || '') !== (mobile || '') && (
                <Button
                  type="button"
                  variant="secondary"
                  className="rounded-xl shrink-0"
                  disabled={savingMobile}
                  onClick={saveMobileIfNeeded}
                  data-testid="save-mobile-btn"
                >
                  {savingMobile ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                </Button>
              )}
            </div>
            {(localMobile || mobile) && !mobileVerified && (
              <div className="flex justify-end">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className={verifyBtnClass}
                  disabled={!canVerifyMobile}
                  onClick={openMobileVerify}
                  data-testid="verify-mobile-btn"
                >
                  Verify mobile
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {showGateMessage && gateMessage && (
        <div
          className="rounded-xl border border-amber-200 bg-amber-50/90 px-3.5 py-2.5 text-sm text-amber-800 flex items-start gap-2 animate-in fade-in slide-in-from-top-1"
          data-testid="verification-gate-message"
          role="alert"
        >
          <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
          <span>{gateMessage}</span>
        </div>
      )}

      <OtpVerifyDialog
        open={emailDialog}
        onOpenChange={setEmailDialog}
        channel="email"
        destinationHint={email}
        onVerified={handleVerified}
      />
      <OtpVerifyDialog
        open={mobileDialog}
        onOpenChange={setMobileDialog}
        channel="mobile"
        destinationHint={localMobile || mobile}
        mobileValue={localMobile || mobile}
        onVerified={handleVerified}
      />
    </div>
  );
}

ContactVerificationControls.propTypes = {
  email: PropTypes.string,
  mobile: PropTypes.string,
  emailVerified: PropTypes.bool,
  mobileVerified: PropTypes.bool,
  mobileEditable: PropTypes.bool,
  onMobileChange: PropTypes.func,
  onVerificationChange: PropTypes.func,
  showGateMessage: PropTypes.bool,
  gateMessage: PropTypes.string,
  mode: PropTypes.oneOf(['onboarding', 'profile']),
};

export { VerifiedChip, UnverifiedChip };
